import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { copy, PRODUCT_LINES } from "@wheels-crm/shared-types";
import type { Database, ProductLine } from "@wheels-crm/shared-types";
import { supabase } from "../lib/supabaseClient";
import { Input } from "../components/ui/Input";

type ClientRow = Database["public"]["Tables"]["clients"]["Row"];
type OrderRow = Database["public"]["Tables"]["orders"]["Row"];
type ClientWithOrders = ClientRow & { orders: OrderRow[] };

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

export function ClientsPage() {
  const [clients, setClients] = useState<ClientWithOrders[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const productLine: ProductLine =
    searchParams.get("line") === "steering_wheels" ? "steering_wheels" : "wheels";

  function setProductLine(line: ProductLine) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("line", line);
      return next;
    });
  }

  useEffect(() => {
    supabase
      .from("clients")
      .select("*, orders(*)")
      .order("name", { ascending: true })
      .then(({ data }) => {
        setClients((data ?? []) as unknown as ClientWithOrders[]);
        setLoading(false);
      });
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return clients.filter((c) => {
      if (c.product_line !== productLine) return false;
      if (!q) return true;
      return c.name.toLowerCase().includes(q) || c.phone.includes(q);
    });
  }, [clients, query, productLine]);

  return (
    <div>
      <div className="page-header">
        <h1>{copy.clients.title}</h1>
      </div>

      <div className="product-line-switch">
        {PRODUCT_LINES.map((line) => (
          <button
            key={line}
            type="button"
            className={`product-line-tab ${productLine === line ? "product-line-tab-active" : ""}`}
            onClick={() => setProductLine(line)}
          >
            {copy.productLine[line]}
          </button>
        ))}
      </div>

      <div className="search-bar">
        <Input
          placeholder={copy.clients.searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {loading ? (
        <p>Завантаження…</p>
      ) : filtered.length === 0 ? (
        <p className="empty-state">{copy.emptyState}</p>
      ) : (
        <div className="orders-table-wrap">
          <table className="resizable-table" style={{ minWidth: 560 }}>
            <thead>
              <tr>
                <th>{copy.table.client}</th>
                <th>{copy.table.phone}</th>
                <th>{copy.clients.orderCountColumn}</th>
                <th>{copy.clients.lastOrderColumn}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const sortedOrders = [...c.orders].sort((a, b) => b.created_at.localeCompare(a.created_at));
                const last = sortedOrders[0];
                return (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.phone}</td>
                    <td>{c.orders.length}</td>
                    <td>
                      {last ? (
                        <Link to={`/orders/${last.id}`}>
                          {last.size_spec ?? last.car_brand ?? copy.productLine[last.product_line]} ·{" "}
                          {formatDate(last.created_at)}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
